import { useState, useEffect } from 'react';

export function useLocalStorage<T>(key: string, initialValue: T, legacyKeys: string[] = []) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      if (item) return JSON.parse(item) as T;
      for (const legacy of legacyKeys) {
        const old = window.localStorage.getItem(legacy);
        if (old) {
          window.localStorage.setItem(key, old);
          window.localStorage.removeItem(legacy);
          return JSON.parse(old) as T;
        }
      }
      return initialValue;
    } catch {
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(storedValue));
    } catch {
      // ignore
    }
  }, [key, storedValue]);

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === key) {
        try {
          const item = window.localStorage.getItem(key);
          setStoredValue(item ? (JSON.parse(item) as T) : initialValue);
        } catch {
          // ignore
        }
      }
    };
    const handleCustom = (e: Event) => {
      const custom = e as CustomEvent;
      if (custom.detail?.key === key) {
        try {
          const item = window.localStorage.getItem(key);
          setStoredValue(item ? (JSON.parse(item) as T) : initialValue);
        } catch {
          // ignore
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('local-storage-change', handleCustom);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('local-storage-change', handleCustom);
    };
  }, [key, initialValue]);

  const setValue = (value: T | ((val: T) => T)) => {
    const valueToStore = value instanceof Function ? value(storedValue) : value;
    setStoredValue(valueToStore);
  };

  return [storedValue, setValue] as const;
}
